import React, { useRef, useState } from "react";
import { Upload, X, Image as ImageIcon, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

/**
 * Image uploader réutilisable :
 *   <ImageUpload value={url} onChange={(url) => setUrl(url)} kind="arnak" aspect="square|wide|portrait" label="Logo" />
 */
export const ImageUpload = ({ value, onChange, kind = "arnak", aspect = "square", label = "Image", hint, testId }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Seules les images sont acceptées"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Image trop lourde (max 5 Mo)"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", kind);
      const { data } = await api.post("/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Image téléversée ✨");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur upload");
    } finally {
      setUploading(false);
    }
  };

  const aspectCls = aspect === "wide" ? "aspect-[16/9]" : aspect === "portrait" ? "aspect-[3/4]" : "aspect-square";

  return (
    <div data-testid={testId || `upload-${kind}`}>
      {label && <p className="text-[11px] uppercase tracking-widest text-white/55 font-bold mb-1">{label}</p>}
      <div
        onClick={() => !uploading && inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); }}
        onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files?.[0]); }}
        className={`relative ${aspectCls} max-h-44 rounded-xl border-2 border-dashed cursor-pointer overflow-hidden transition ${value ? "border-white/10 bg-black/40" : "border-white/15 bg-white/[0.02] hover:border-yellow-400/50"}`}
      >
        {value ? (
          <>
            <img src={value} alt="" className="w-full h-full object-contain bg-zinc-900" />
            <div className="absolute inset-0 bg-black/0 hover:bg-black/60 transition flex items-center justify-center opacity-0 hover:opacity-100">
              <span className="text-xs font-bold inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/15 backdrop-blur"><Upload size={12} /> Remplacer</span>
            </div>
            <button onClick={(e) => { e.stopPropagation(); onChange(""); }} type="button"
              className="absolute top-2 right-2 p-1 rounded-full bg-black/70 hover:bg-red-500/80 transition" title="Retirer">
              <X size={12} />
            </button>
          </>
        ) : uploading ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-yellow-400">
            <Loader2 size={22} className="animate-spin" />
            <span className="text-xs font-bold">Envoi…</span>
          </div>
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 text-white/45">
            <ImageIcon size={22} />
            <span className="text-xs font-bold">Cliquer ou glisser une image</span>
            {hint && <span className="text-[10px] text-white/30">{hint}</span>}
          </div>
        )}
        <input ref={inputRef} type="file" accept="image/*" className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])} />
      </div>
    </div>
  );
};

export default ImageUpload;
